import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPanel.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";
import { Table, Spinner, Alert, Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const API_BASE_URL = "https://api.mitradingjp.com/client_transactions.php";

const ClientAccounts = () => {
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [userId, setUserId] = useState("");
    const [userFullName, setUserFullName] = useState("");
    const [balanceSheet, setBalanceSheet] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const storedUserType = localStorage.getItem("usertype");
        const storedUserId = localStorage.getItem("userId");
        const storedFName = localStorage.getItem("fname");
        const storedLName = localStorage.getItem("lname");

        if (!storedUserType) {
            navigate("/login");
        } else {
            setUserType(storedUserType);
            setUserId(storedUserId);
            if (storedFName && storedLName) {
                setUserFullName(`${storedFName} ${storedLName}`);
            } else {
                setUserFullName(storedFName || '');
            }
        }

        if (storedUserId) {
            fetchBalanceSheet(storedUserId);
        } else {
            setError("User ID not found in localStorage");
            setLoading(false);
        }
    }, [navigate]);

    const fetchBalanceSheet = async (userId) => {
        try {
            setLoading(true);
            setError("");
            setBalanceSheet([]);

            const url = `${API_BASE_URL}?action=getClientTransactions&clientId=${userId}`;
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                console.log("API Response:", result);
                setBalanceSheet(result.data || []);
                if (result.data.length === 0) {
                    setError("No transactions found");
                }
            } else {
                setError(result.message || "Failed to fetch transactions");
            }
        } catch (err) {
            setError("Failed to connect to the server. Please try again later.");
            console.error("Fetch transactions error:", err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const logout = () => {
        localStorage.clear();
        navigate("/login");
    };

    let runningBalance = 0;
    const balanceSheetWithRunningBalance = balanceSheet.map((transaction) => {
        const received = parseFloat(transaction.received_amount || 0);
        const allocated = parseFloat(transaction.allocated_amount || 0);
        runningBalance = runningBalance + received - allocated;
        return { ...transaction, balance: runningBalance };
    });

    const totalBalance = balanceSheetWithRunningBalance.length > 0 
        ? balanceSheetWithRunningBalance[balanceSheetWithRunningBalance.length - 1].balance 
        : 0;

    const downloadExcel = () => {
        // Prepare data for Excel
        const excelData = balanceSheetWithRunningBalance.map((transaction, index) => ({
            '#': index + 1,
            Date: formatDate(transaction.date),
            'Stock ID': transaction.stock_id,
            'Received Amount ($)': parseFloat(transaction.received_amount || 0),
            'Allocated Amount ($)': parseFloat(transaction.allocated_amount || 0),
            'Balance ($)': parseFloat(transaction.balance)
        }));

        // Add total row
        excelData.push({
            '#': '',
            Date: '',
            'Stock ID': '',
            'Received Amount ($)': '',
            'Allocated Amount ($)': 'Total:',
            'Balance ($)': totalBalance
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        // Create workbook and append the worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Transaction History");
        // Generate Excel file and trigger download
        XLSX.writeFile(wb, `Transaction_History_${userFullName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <AdministratorSidebar />}
                {userType === "admin" && <AdminSidebar />}
                {userType === "user" && <UserSidebar />}
            </div>

            <div className="admin-content">
                <div className="adminnavbar">
                    <h1>Welcome to the Admin Panel, {userFullName}</h1>
                    <button onClick={logout} className="logoutbtn">
                        Logout
                    </button>
                </div>

                <div className="mainsection">
                    <h3>Your Transaction History</h3>

                    {error && (
                        <Alert variant={balanceSheet.length === 0 ? "info" : "danger"} className="mt-3">
                            {error}
                            <button 
                                className="btn btn-sm btn-primary ms-2"
                                onClick={() => fetchBalanceSheet(userId)}
                            >
                                Retry
                            </button>
                        </Alert>
                    )}

                    {loading ? (
                        <div className="text-center my-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p>Loading your transactions...</p>
                        </div>
                    ) : balanceSheetWithRunningBalance.length > 0 ? (
                        <div className="balance-sheet mt-4">
                            <div className="d-flex justify-content-end mb-2">
                                <Button
                                    variant="primary"
                                    onClick={downloadExcel}
                                    style={{ backgroundColor: '#007bff', borderColor: '#007bff' }}
                                >
                                    Download Excel
                                </Button>
                            </div>
                            <h5>Transaction History</h5>
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Date</th>
                                        <th>Stock ID</th>
                                        <th>Received Amount ($)</th>
                                        <th>Allocated Amount ($)</th>
                                        <th>Balance ($)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {balanceSheetWithRunningBalance.map((transaction, index) => (
                                        <tr key={transaction.id}>
                                            <td>{index + 1}</td>
                                            <td>{formatDate(transaction.date)}</td>
                                            <td>{transaction.stock_id}</td>
                                            <td>${parseFloat(transaction.received_amount || 0).toLocaleString()}</td>
                                            <td>${parseFloat(transaction.allocated_amount || 0).toLocaleString()}</td>
                                            <td>${parseFloat(transaction.balance).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="5" className="text-end"><strong>Total:</strong></td>
                                        <td><strong>${totalBalance.toLocaleString()}</strong></td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center mt-4">
                            <p>No transactions found.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientAccounts;